'use client';

import { useEffect, useState } from 'react';
import {
  Minus,
  Mic,
  MicOff,
  PauseCircle,
  PlayCircle,
  PhoneOff,
  PhoneForwarded,
  Hash,
  PhoneIncoming,
  PhoneOutgoing,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ActiveCall, WebphoneState } from '@/hooks/voice/webphone-types';
import { useMicLevel } from './use-mic-level';
import { CustomerCard } from './customer-card';
import { isInternalExtension } from '@/hooks/voice/phone-format';

interface Props {
  call: ActiveCall;
  state: WebphoneState;
  /** Protocolo do atendimento (ex.: "GKHUB202671") — exibido durante a ligação. */
  protocolo?: string | null;
  /** Volta pra bubble — chamada continua, só esconde o painel */
  onMinimize: () => void;
  onHangup: () => void;
  onToggleMute: () => void;
  onToggleHold: () => void;
  onSendDtmf: (digit: string) => void;
  /** Abre o dialog de transferência. Desabilitado fora de in_call. */
  onTransfer: () => void;
}

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

function formatDuration(ms: number) {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function WebphonePanelActive({
  call,
  state,
  protocolo,
  onMinimize,
  onHangup,
  onToggleMute,
  onToggleHold,
  onSendDtmf,
  onTransfer,
}: Props) {
  const [showNumpad, setShowNumpad] = useState(false);
  const [now, setNow] = useState(Date.now());
  const micLevel = useMicLevel(state === 'in_call' && !call.muted);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const duration = call.answeredAt ? now - call.answeredAt : 0;
  const stateLabel =
    state === 'calling'
      ? 'Discando...'
      : state === 'ringing'
        ? 'Tocando'
        : 'Em chamada';

  return (
    <div className="relative w-80 rounded-2xl bg-card border shadow-2xl overflow-hidden">
      {/* Header com avatar e estado */}
      <div className="px-6 pt-6 pb-4 bg-gradient-to-b from-primary/10 to-transparent text-center">
        <div className="absolute top-3 right-3 flex items-center gap-0.5">
          <button
            type="button"
            onClick={onMinimize}
            className="p-1 rounded hover:bg-accent"
            aria-label="Minimizar"
            title="Minimizar"
          >
            <Minus className="h-4 w-4" />
          </button>
        </div>

        <div className="flex justify-center mb-3">
          <div
            className={cn(
              'h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center',
              state === 'in_call' && 'ring-4 ring-primary/30 animate-pulse',
            )}
          >
            {state === 'calling' ? (
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            ) : call.direction === 'outbound' ? (
              <PhoneOutgoing className="h-8 w-8 text-primary" />
            ) : (
              <PhoneIncoming className="h-8 w-8 text-primary" />
            )}
          </div>
        </div>

        <p className="font-semibold">{call.remoteName ?? call.remoteNumber}</p>
        {call.remoteName && (
          <p className="text-xs text-muted-foreground font-mono mt-0.5">{call.remoteNumber}</p>
        )}
        {protocolo && (
          <p className="mt-1 text-xs text-muted-foreground">
            Protocolo{' '}
            <span className="font-mono font-medium text-foreground">{protocolo}</span>
          </p>
        )}

        <div className="mt-2 flex items-center justify-center gap-2 text-xs">
          <span className="text-muted-foreground">{stateLabel}</span>
          {state === 'in_call' && (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="font-mono text-foreground">{formatDuration(duration)}</span>
            </>
          )}
        </div>

        <div className="mt-3 text-left">
          {/* Só busca cliente no ERP pra número externo — ramal interno
              (ramal↔ramal) dá match espúrio no IXC e mostra cliente errado. */}
          {isInternalExtension(call.remoteNumber) ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              Ramal interno {call.remoteNumber}
            </p>
          ) : (
            <CustomerCard phone={call.remoteNumber} />
          )}
        </div>

        {/* Mic level visualizer — 5 barras respondendo ao volume do mic */}
        {state === 'in_call' && (
          <div className="flex items-end gap-1 h-12 mt-3 justify-center">
            {[0, 1, 2, 3, 4].map((i) => {
              const threshold = (i + 1) * 20;
              const active = !call.muted && micLevel >= threshold;
              return (
                <div
                  key={i}
                  className={cn(
                    'w-1.5 rounded-full transition-all duration-100',
                    active ? 'bg-emerald-500' : 'bg-muted',
                  )}
                  style={{ height: active ? `${20 + i * 6}px` : '8px' }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Numpad in-call (DTMF) */}
      {showNumpad && state === 'in_call' && (
        <div className="border-t bg-muted/40 p-3">
          <div className="grid grid-cols-3 gap-2">
            {KEYS.flat().map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => onSendDtmf(k)}
                className="h-10 rounded-lg bg-background hover:bg-accent font-medium active:scale-95 transition-transform"
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Controles */}
      <div className="grid grid-cols-3 gap-2 p-4">
        <Button
          variant={call.muted ? 'default' : 'outline'}
          onClick={onToggleMute}
          className="flex-col h-16 gap-1"
          disabled={state !== 'in_call'}
        >
          {call.muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          <span className="text-xs">{call.muted ? 'Sem áudio' : 'Silenciar'}</span>
        </Button>
        <Button
          variant={call.onHold ? 'default' : 'outline'}
          onClick={onToggleHold}
          className="flex-col h-16 gap-1"
          disabled={state !== 'in_call'}
        >
          {call.onHold ? <PlayCircle className="h-5 w-5" /> : <PauseCircle className="h-5 w-5" />}
          <span className="text-xs">{call.onHold ? 'Retomar' : 'Pausar'}</span>
        </Button>
        <Button
          variant={showNumpad ? 'default' : 'outline'}
          onClick={() => setShowNumpad((v) => !v)}
          className="flex-col h-16 gap-1"
          disabled={state !== 'in_call'}
        >
          <Hash className="h-5 w-5" />
          <span className="text-xs">Teclas</span>
        </Button>
      </div>

      {/* Transferir */}
      <div className="px-4 pb-2">
        <Button
          variant="outline"
          onClick={onTransfer}
          disabled={state !== 'in_call'}
          className="w-full h-11"
        >
          <PhoneForwarded className="h-5 w-5 mr-2" /> Transferir
        </Button>
      </div>

      {/* Hangup */}
      <div className="px-4 pb-4">
        <Button
          onClick={onHangup}
          className="w-full h-12 bg-red-600 hover:bg-red-700 text-white"
        >
          <PhoneOff className="h-5 w-5 mr-2" /> Encerrar
        </Button>
      </div>
    </div>
  );
}
